// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import LinkDeviceCardModal from "../../LinkDeviceCardModal/LinkDeviceCardModal";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import { LinkIcon, UnlinkIcon } from "src/assets/icons";

// Types
import { DeviceType } from "src/services/devices/types";
import { ExamType } from "src/services/exams/types";

// Hooks
import { usePatientsData } from "src/services/patients/hooks/usePatientsData";
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useUnlinkExamFromDevice } from "src/services/devices/hooks/useUnlinkExamDevice";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type DeviceCardButtonType = {
  device: DeviceType;
  isLinked: boolean;
  isOutdated: boolean;
};

const DeviceCardButton = ({
  device,
  isLinked,
  isOutdated,
}: DeviceCardButtonType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { elevatedSurface } = getSurfaceTokens(theme);
  const unlinkBackground = isDark ? "#3B868E" : elevatedSurface;
  const unlinkBackgroundHover = isDark ? "#327279" : "#0C0F24";
  const { clinicInfo } = useClinicContext();

  const { data: patientsData } = usePatientsData(clinicInfo?.id);
  const { mutateAsync: linkDeviceMutate } = useLinkExamDevice(clinicInfo?.id);
  const { mutateAsync: unlinkDeviceMutate } = useUnlinkExamFromDevice(
    clinicInfo?.id
  );

  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [openUnlinkModal, setOpenUnlinkModal] = useState(false);

  const isExamInProgress = useMemo(() => {
    if (!device || !device?.id || !device?.associatedExam) return false;
    return device?.deviceStatus?.stage?.stageType === "Exam";
  }, [device]);

  const unlinkDevice = useCallback(async () => {
    await unlinkDeviceMutate(device?.id, {
      onSuccess: () => setOpenUnlinkModal(false),
    });
  }, [device?.id, unlinkDeviceMutate]);

  const linkDevice = useCallback(
    async (exam: ExamType) => {
      await linkDeviceMutate(
        { deviceId: device?.id, exam },
        { onSuccess: () => setOpenLinkModal(false) }
      );
    },
    [device?.id, linkDeviceMutate]
  );

  return (
    <Grid item display="flex">
      {isLinked ? (
        <Button
          sx={{
            textTransform: "capitalize",
            height: `${UI_SIZES.inputHeight}px`,
            width: "100%",
            fontSize: "16px",
            backgroundColor: unlinkBackground,
            color: "#FFF",
            border: "none",
            gap: "8px",
            ":disabled": { color: "#FFFFFF80" },
            "&:hover": {
              backgroundColor: unlinkBackgroundHover,
              border: "none",
            },
          }}
          aria-label="unlinked-button"
          disabled={isExamInProgress}
          onClick={() => setOpenUnlinkModal(true)}
        >
          {t("Unlink")}
          <UnlinkIcon />
        </Button>
      ) : isOutdated ? (
        <Button
          sx={{
            textTransform: "capitalize",
            height: `${UI_SIZES.inputHeight}px`,
            width: "100%",
            fontSize: "16px",
            backgroundColor: "#87201A",
            color: "#FFF",
            border: "none",
            "&:disabled": {
              border: "none",
              backgroundColor: "#87201A",
              color: "#FFF",
            },
          }}
          disabled
          aria-label="outdated-button"
        >
          {t("Outdated")}
        </Button>
      ) : (
        <Button
          sx={{
            textTransform: "capitalize",
            height: `${UI_SIZES.inputHeight}px`,
            width: "100%",
            fontSize: "16px",
            backgroundColor: "#E2772E",
            color: "#FFF",
            border: "none",
            gap: "8px",
            "&:hover": {
              backgroundColor: "#CE631A",
              color: "#FFF",
              border: "none",
            },
          }}
          onClick={() => setOpenLinkModal(true)}
          aria-label="link-button"
        >
          {t("Link")}
          <LinkIcon />
        </Button>
      )}

      <LinkDeviceCardModal
        open={openLinkModal}
        onClose={() => setOpenLinkModal(false)}
        onConfirm={linkDevice}
        patients={patientsData ?? []}
      />

      {/* Unlink Device Modal */}
      <SharedModal
        title={t("Unlink Device?")}
        modalDescription={t(
          "Are you sure you want to unlink the exam from this device?"
        )}
        confirmButtonText={t("Unlink")}
        open={openUnlinkModal}
        onClose={() => setOpenUnlinkModal(false)}
        onConfirm={unlinkDevice}
      />
    </Grid>
  );
};

export default DeviceCardButton;
